import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_API_URL;

interface Scout {
  id: number;
  name: string;
  email: string;
  created_at: string;
  hdcp_base_average: number;
  hdcp_percentage: number;
  hdcp_max: number;
}

const MasterScouts: React.FC = () => {
  const { t } = useTranslation();
  
  const [scouts, setScouts] = useState<Scout[]>([]);
  const [loading, setLoading] = useState(true);

  // Edit scout modal
  const [showEditScoutModal, setShowEditScoutModal] = useState(false);
  const [editScoutTarget, setEditScoutTarget] = useState<Scout | null>(null);
  const [editScoutForm, setEditScoutForm] = useState({ name: '', email: '' });
  const [savingScoutEdit, setSavingScoutEdit] = useState(false);
  const [editScoutError, setEditScoutError] = useState<string | null>(null);

  // Set password modal for scout
  const [showScoutPwdModal, setShowScoutPwdModal] = useState(false);
  const [scoutPwdTarget, setScoutPwdTarget] = useState<{ id: number; name: string; email: string } | null>(null);
  const [scoutPwdValue, setScoutPwdValue] = useState('');
  const [scoutPwdLoading, setScoutPwdLoading] = useState(false);
  const [scoutPwdError, setScoutPwdError] = useState<string | null>(null);
  const [scoutPwdSuccess, setScoutPwdSuccess] = useState(false);

  useEffect(() => {
    fetchScouts();
  }, []);

  const fetchScouts = async () => {
    try {
      setLoading(true);
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/master/scouts/scouts`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setScouts(data.scouts);
      }
    } catch (error) {
      console.error('Failed to fetch scouts:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const openEditScoutModal = (scout: Scout, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditScoutTarget(scout);
    setEditScoutForm({ name: scout.name, email: scout.email });
    setEditScoutError(null);
    setShowEditScoutModal(true);
  };

  const handleSaveScoutEdit = async () => {
    if (!editScoutTarget) return;
    setSavingScoutEdit(true);
    setEditScoutError(null);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/master/scouts/scouts/${editScoutTarget.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editScoutForm)
      });
      if (response.ok) {
        setScouts(prev => prev.map(s => s.id === editScoutTarget.id ? { ...s, ...editScoutForm } : s));
        setShowEditScoutModal(false);
      } else {
        const data = await response.json();
        setEditScoutError(data.error || 'Failed to update scout.');
      }
    } catch {
      setEditScoutError('Network error. Please try again.');
    } finally {
      setSavingScoutEdit(false);
    }
  };

  const openScoutPwdModal = (scout: { id: number; name: string; email: string }, e: React.MouseEvent) => {
    e.stopPropagation();
    setScoutPwdTarget(scout);
    setScoutPwdValue('');
    setScoutPwdError(null);
    setScoutPwdSuccess(false);
    setShowScoutPwdModal(true);
  };

  const handleSetScoutPassword = async () => {
    if (!scoutPwdTarget) return;
    if (!scoutPwdValue || scoutPwdValue.length < 6) {
      setScoutPwdError('Password must be at least 6 characters.');
      return;
    }
    setScoutPwdLoading(true);
    setScoutPwdError(null);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/master/scouts/scouts/${scoutPwdTarget.id}/set-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password: scoutPwdValue })
      });
      if (response.ok) {
        setScoutPwdSuccess(true);
      } else {
        const data = await response.json();
        setScoutPwdError(data.error || 'Failed to set password.');
      }
    } catch {
      setScoutPwdError('Network error. Please try again.');
    } finally {
      setScoutPwdLoading(false);
    }
  };

  return (
    <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 border border-white/20 max-h-[calc(100vh-300px)] overflow-y-auto">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-3xl font-bold text-white">
          {t('superadmin.scouts.title')} ({scouts.length})
        </h3>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-400"></div>
          <p className="text-yellow-200 mt-4">{t('common.loading')}</p>
        </div>
      ) : scouts.length === 0 ? (
        <div className="text-center py-12">
          <span className="text-6xl">🔍</span>
          <p className="text-yellow-200 mt-4 text-lg">{t('superadmin.scouts.noScouts')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {scouts.map((scout) => (
            <div
              key={scout.id}
              className="bg-gradient-to-br from-yellow-600/20 to-orange-600/20 border-2 border-yellow-400/30 rounded-lg p-6 hover:border-yellow-400/60 transition-all"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-14 h-14 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-xl">
                    {scout.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => openEditScoutModal(scout, e)}
                    title="Edit scout"
                    className="text-xs px-2 py-1 bg-yellow-500/20 hover:bg-yellow-500/40 border border-yellow-400/40 text-yellow-300 rounded transition"
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={(e) => openScoutPwdModal(scout, e)}
                    className="text-xs px-2 py-1 bg-orange-500/20 hover:bg-orange-500/40 border border-orange-400/40 text-orange-300 rounded transition"
                  >
                    🔑 Pwd
                  </button>
                  <span className="text-yellow-300 font-mono text-sm">#{scout.id}</span>
                </div>
              </div>

              <h4 className="text-white font-bold text-xl mb-2">{scout.name}</h4>
              
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <span className="text-yellow-400">📧</span>
                  <p className="text-yellow-200 text-sm">{scout.email}</p>
                </div>
                
                <div className="bg-white/10 p-2 rounded mt-2">
                  <p className="text-yellow-200 text-xs font-semibold mb-1">{t('superadmin.scouts.handicapSettings')}:</p>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <p className="text-yellow-300">{t('superadmin.scouts.base')}: {scout.hdcp_base_average}</p>
                    <p className="text-yellow-300">%: {scout.hdcp_percentage}</p>
                    <p className="text-yellow-300">{t('superadmin.scouts.max')}: {scout.hdcp_max}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <span className="text-yellow-400">📅</span>
                  <p className="text-yellow-300 text-xs">{t('superadmin.scouts.joined')}: {formatDate(scout.created_at)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Scout Modal */}
      {showEditScoutModal && editScoutTarget && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowEditScoutModal(false)}>
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 max-w-md w-full mx-4 border border-white/20" onClick={e => e.stopPropagation()}>
            <h2 className="text-2xl font-bold text-white mb-6">✏️ Edit Scout</h2>
            {editScoutError && (
              <p className="text-red-300 text-sm mb-4 bg-red-500/20 p-3 rounded-lg">{editScoutError}</p>
            )}
            <div className="space-y-4">
              <div>
                <label className="block text-yellow-200 text-sm font-semibold mb-1">Name</label>
                <input
                  type="text"
                  value={editScoutForm.name}
                  onChange={e => setEditScoutForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                />
              </div>
              <div>
                <label className="block text-yellow-200 text-sm font-semibold mb-1">Email</label>
                <input
                  type="email"
                  value={editScoutForm.email}
                  onChange={e => setEditScoutForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSaveScoutEdit}
                disabled={savingScoutEdit}
                className="flex-1 bg-yellow-600 hover:bg-yellow-700 disabled:opacity-50 text-white py-3 rounded-lg font-bold transition"
              >
                {savingScoutEdit ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => setShowEditScoutModal(false)}
                className="flex-1 bg-white/10 hover:bg-white/20 text-white py-3 rounded-lg font-bold transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Set Password Modal for Scout */}
      {showScoutPwdModal && scoutPwdTarget && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowScoutPwdModal(false)}>
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 max-w-md w-full mx-4 border border-white/20" onClick={e => e.stopPropagation()}>
            <h2 className="text-2xl font-bold text-white mb-2">🔑 Set Password</h2>
            <p className="text-yellow-200 text-sm mb-6">
              Setting a password for <strong className="text-white">{scoutPwdTarget.name}</strong>{' '}
              <span className="text-yellow-300">({scoutPwdTarget.email})</span>
            </p>
            {scoutPwdSuccess ? (
              <div className="text-center py-4">
                <p className="text-green-300 text-lg font-semibold mb-2">✅ Password set successfully!</p>
                <p className="text-yellow-200 text-sm mb-6">An email was sent to {scoutPwdTarget.email} with their new password.</p>
                <button
                  onClick={() => setShowScoutPwdModal(false)}
                  className="w-full bg-yellow-600 hover:bg-yellow-700 text-white py-3 rounded-lg font-bold transition"
                >
                  Done
                </button>
              </div>
            ) : (
              <>
                {scoutPwdError && (
                  <p className="text-red-300 text-sm mb-4 bg-red-500/20 p-3 rounded-lg">{scoutPwdError}</p>
                )}
                <div className="mb-6">
                  <label className="block text-yellow-200 text-sm font-semibold mb-1">New Password</label>
                  <input
                    type="text"
                    value={scoutPwdValue}
                    onChange={e => setScoutPwdValue(e.target.value)}
                    placeholder="Min. 6 characters"
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                  />
                  <p className="text-yellow-300 text-xs mt-1">The scout will be emailed their new password and told to change it.</p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleSetScoutPassword}
                    disabled={scoutPwdLoading}
                    className="flex-1 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white py-3 rounded-lg font-bold transition"
                  >
                    {scoutPwdLoading ? 'Setting...' : 'Set Password'}
                  </button>
                  <button
                    onClick={() => setShowScoutPwdModal(false)}
                    className="flex-1 bg-white/10 hover:bg-white/20 text-white py-3 rounded-lg font-bold transition"
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterScouts;
